/**
 * REFUND & CANCELLATION POLICY PAGE
 * 
 * Clear policy for subscription refunds and cancellations
 * Should be reviewed by legal counsel before launch
 */

import { RotateCcw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PageHeader } from '../components/common';

export default function RefundPolicyPage() {
  const lastUpdated = 'January 2025';

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <PageHeader
          icon={RotateCcw}
          title="Refund & Cancellation Policy"
          subtitle={`Last updated: ${lastUpdated}`}
          className="mb-6"
        />

        {/* Content */}
        <div className="glass-card p-8 space-y-8 font-body">
          <section>
            <h2 className="text-xl font-bold text-glass mb-3">Overview</h2>
            <p className="text-glass-secondary leading-relaxed">
              We want you to be happy with Polish ALL the Things! This policy explains how refunds and 
              cancellations work for our paid storage subscription plans. The free tier is always free 
              and requires no payment.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-glass mb-3">Free Trial & Free Tier</h2>
            <p className="text-glass-secondary leading-relaxed">
              All users start with a free tier that includes generous storage limits. You can use 
              Polish ALL the Things indefinitely at the free tier without ever providing payment 
              information. We encourage you to try all features before upgrading to a paid plan.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-glass mb-3">Monthly Subscriptions</h2>
            <p className="text-glass-secondary leading-relaxed mb-3">
              Monthly subscriptions are billed at the beginning of each billing period. Because you 
              have immediate access to increased storage and features upon payment:
            </p>
            <ul className="list-disc list-inside text-glass-secondary space-y-2 ml-4">
              <li><strong>First 7 days:</strong> Full refund available if you're not satisfied, no questions asked</li>
              <li><strong>After 7 days:</strong> No refunds for the current billing period, but you may cancel anytime to prevent future charges</li>
              <li><strong>Cancellation:</strong> You retain access to paid features until the end of your current billing period</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-glass mb-3">How to Cancel</h2>
            <p className="text-glass-secondary leading-relaxed mb-3">
              You can cancel your subscription at any time through your account Settings. Here's what happens:
            </p>
            <ul className="list-disc list-inside text-glass-secondary space-y-2 ml-4">
              <li>Your subscription will not renew at the end of the current billing period</li>
              <li>You keep access to paid features until your current period ends</li>
              <li>Your data is never deleted automatically — you'll just be moved to the free tier</li>
              <li>If you exceed free tier limits, you'll need to reduce storage or upgrade again to add new items</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-glass mb-3">What Happens If I Downgrade?</h2>
            <p className="text-glass-secondary leading-relaxed mb-3">
              If you downgrade to a lower storage tier (including the free tier) and your current usage 
              exceeds your new plan's limits:
            </p>
            <ul className="list-disc list-inside text-glass-secondary space-y-2 ml-4">
              <li><strong>30-day grace period:</strong> You'll have 30 days to reduce your storage to fit within your new plan</li>
              <li><strong>During this time:</strong> You can view and manage all your existing items, but cannot add new ones</li>
              <li><strong>Export your data:</strong> Use the export feature in Settings to download your collection data and photos before making changes</li>
              <li><strong>After 30 days:</strong> If you're still over the limit, we'll begin removing your oldest items until your storage is within your new plan's allowance</li>
              <li><strong>We'll remind you:</strong> You'll receive email notifications at 7 days, 3 days, and 1 day before any automatic removal begins</li>
            </ul>
            <p className="text-glass-secondary leading-relaxed mt-3">
              We strongly encourage you to export your data and manually choose which items to remove 
              before the grace period ends, so you stay in control of your collection.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-glass mb-3">What Happens If I Cancel Completely?</h2>
            <p className="text-glass-secondary leading-relaxed mb-3">
              If you cancel your subscription entirely:
            </p>
            <ul className="list-disc list-inside text-glass-secondary space-y-2 ml-4">
              <li>You retain access to paid features until the end of your current billing period</li>
              <li>After that, you'll be moved to the free tier</li>
              <li>The same 30-day grace period applies if you're over free tier limits</li>
              <li>Your account is never deleted unless you explicitly request it in Settings</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-glass mb-3">Requesting a Refund</h2>
            <p className="text-glass-secondary leading-relaxed mb-3">
              To request a refund within the eligible period:
            </p>
            <ul className="list-disc list-inside text-glass-secondary space-y-2 ml-4">
              <li>Email us at <a href="mailto:hello@polishallthethings.com" className="text-glass hover:underline">hello@polishallthethings.com</a></li>
              <li>Include your account email and reason for the refund</li>
              <li>We'll process eligible refunds within 5-10 business days</li>
              <li>Refunds are returned to your original payment method</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-glass mb-3">Exceptions</h2>
            <p className="text-glass-secondary leading-relaxed mb-3">
              We may offer refunds outside the standard policy in cases of:
            </p>
            <ul className="list-disc list-inside text-glass-secondary space-y-2 ml-4">
              <li>Service outages or technical issues that significantly impacted your use</li>
              <li>Billing errors on our part</li>
              <li>Other exceptional circumstances at our discretion</li>
            </ul>
            <p className="text-glass-secondary leading-relaxed mt-3">
              We review these requests on a case-by-case basis. Please contact us to discuss your situation.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-glass mb-3">Price Changes</h2>
            <p className="text-glass-secondary leading-relaxed">
              If we change subscription prices, existing subscribers will be notified at least 30 days 
              in advance. Your current rate is locked in until your next renewal after the change takes 
              effect, giving you time to decide whether to continue.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-glass mb-3">Donations (Buy Me a Coffee)</h2>
            <p className="text-glass-secondary leading-relaxed">
              Donations made through our "Buy Me a Coffee" or similar tip features are voluntary 
              contributions to support development. These are non-refundable as they do not provide 
              any additional features or services beyond showing appreciation.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-glass mb-3">Contact Us</h2>
            <p className="text-glass-secondary leading-relaxed">
              Questions about refunds or cancellations? We're here to help!{' '}
              <a href="mailto:hello@polishallthethings.com" className="text-glass hover:underline">
                hello@polishallthethings.com
              </a>
            </p>
          </section>

          {/* Related Links */}
          <section className="pt-4 border-t border-glass-border">
            <p className="text-glass-muted text-sm">
              Related policies:{' '}
              <Link to="/terms" className="text-glass hover:underline">Terms of Service</Link>
              {' · '}
              <Link to="/privacy" className="text-glass hover:underline">Privacy Policy</Link>
              {' · '}
              <Link to="/copyright" className="text-glass hover:underline">Copyright Policy</Link>
            </p>
          </section>
        </div>

        {/* Back to top */}
        <div className="text-center mt-8">
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="text-on-bg font-body"
          >
            ↑ Back to top
          </button>
        </div>
      </div>
    </div>
  );
}
